import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { AlertCircle } from 'lucide-react';

interface ReportUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string, additionalInfo: string) => Promise<void>;
  userName?: string;
}

const REPORT_REASONS = [
  { value: 'inappropriate_messages', label: 'Inappropriate Messages' },
  { value: 'harassment', label: 'Harassment or Abuse' },
  { value: 'fake_profile', label: 'Fake Profile / Misleading Information' },
  { value: 'spam', label: 'Spam or Advertising' },
  { value: 'scam', label: 'Scam or Fraud Attempt' },
  { value: 'underage', label: 'Underage User' },
  { value: 'other', label: 'Other' }
];

export function ReportUserModal({ isOpen, onClose, onSubmit, userName }: ReportUserModalProps) {
  const [selectedReason, setSelectedReason] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!selectedReason) {
      setError('Please select a reason for reporting');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      await onSubmit(selectedReason, additionalInfo);
      // Reset form
      setSelectedReason('');
      setAdditionalInfo('');
      onClose();
    } catch (err) {
      setError('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedReason('');
      setAdditionalInfo('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            Report User
          </DialogTitle>
          <DialogDescription>
            {userName ? `Report ${userName} for inappropriate behavior` : 'Report this user for inappropriate behavior'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Reason Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Reason for reporting *</Label>
            <RadioGroup value={selectedReason} onValueChange={setSelectedReason}>
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                {REPORT_REASONS.map((reason) => (
                  <div key={reason.value} className="flex items-start space-x-2 rounded-md border border-gray-200 p-3 hover:bg-gray-50 transition-colors">
                    <RadioGroupItem value={reason.value} id={reason.value} className="mt-0.5" />
                    <Label
                      htmlFor={reason.value}
                      className="flex-1 cursor-pointer text-sm font-normal leading-tight"
                    >
                      {reason.label}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Additional Information */}
          <div className="space-y-2">
            <Label htmlFor="additional-info" className="text-sm font-medium">
              Additional Information (Optional)
            </Label>
            <Textarea
              id="additional-info"
              placeholder="Please provide any additional details that might help us investigate this report..."
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-gray-500">
              Provide specific details about the issue to help us review your report.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Important Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="text-xs text-amber-800">
              <strong>Important:</strong> False reports may result in action against your account. 
              All reports are reviewed by our team and appropriate action will be taken.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedReason}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}